/**
 * AI CONTEXT
 * Purpose: data for the Quality Check wall (JARVIS-128) — lists the
 *   curricula that have a class this semester, resolves the selected
 *   curriculum's roster through enrollment, joins class/curriculum docs for
 *   filters, and reads student docs from the already-global students atom
 *   (same subscription as the rest of the app).
 * Key deps: Firebase.variableQuery / getDocumentsByIds (chunked `in` reads for
 *   classes/curricula only), qualityCheckHelpers (pure roster + join logic),
 *   useGetAllStudents.
 * Referenced by: QualityCheckView.
 *
 * Student docs are NOT re-fetched here: FirebaseContextProvider already
 * maintains `studentAtoms.allDocsAtom`, and QC filters that list to the
 * curriculum roster. When a student doc changes in the atom, the wall updates
 * without a second student read. Class/curriculum docs are still one-shot
 * reads when the curriculum changes or the user retries after an error — QC
 * is a review surface, not a live dashboard, and standing listeners over
 * semester classes are a separate cost from student docs.
 */
import * as Sentry from "@sentry/react"
import { groupBy, uniq } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import Firebase from "#classes/Firebase"
import {
  buildCurriculumCatalog,
  type CurriculumRoster,
  getMissingArtworkClassIds,
  indexById,
  type QualityCheckCurriculumOption,
  resolveCurriculumRoster,
} from "#components/views/QualityCheck/qualityCheckHelpers"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { useGetAllStudents } from "#hooks/jotai/useGetStudents"
import { getCurrentSemester } from "#utils/semesterHelpers"

export const QUALITY_CHECK_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
} as const

export type QualityCheckStatus =
  (typeof QUALITY_CHECK_STATUS)[keyof typeof QUALITY_CHECK_STATUS]

export interface QualityCheckLoadStats {
  /** Students enrolled in a class on the curriculum. */
  rosterSize: number
  /** Classes on the curriculum this semester. */
  rosterClassCount: number
  /** Roster students found in the global student atom. */
  studentCount: number
  /** Class + curriculum docs read for the filters' joins. */
  classDocCount: number
  curriculumDocCount: number
  /** UTF-8 size of the JSON of every doc kept — an estimate of the wire payload. */
  approxBytes: number
  /** Wall-clock for the whole read sequence. */
  durationMs: number
  loadedAt: number
}

export interface QualityCheckSnapshot {
  curriculumId: string
  semester: string
  students: StudentConstructor[]
  classesById: Record<Uuid, ClassConstructor>
  curriculaById: Record<Uuid, CurriculumConstructor>
  /** Sessions of the roster classes, grouped by class id. One-shot, with the class read. */
  sessionsByClassId: Record<Uuid, ClassSessionConstructor[]>
  stats: QualityCheckLoadStats
}

export interface QualityCheckData {
  status: QualityCheckStatus
  snapshot: QualityCheckSnapshot | null
  error: string | null
  /** Curricula with a class this semester, by name. Loaded with no selection too. */
  curricula: QualityCheckCurriculumOption[]
  /** Re-runs the class/curriculum read for the current curriculum. */
  refresh: () => void
}

const approximateBytes = (value: unknown): number =>
  new TextEncoder().encode(JSON.stringify(value)).length

export interface QualityCheckCatalog {
  semester: string
  semesterClasses: ClassConstructor[]
  curriculaById: Record<Uuid, CurriculumConstructor>
  curricula: QualityCheckCurriculumOption[]
}

export interface QualityCheckJoin {
  curriculumId: string
  semester: string
  roster: CurriculumRoster
  classesById: Record<Uuid, ClassConstructor>
  curriculaById: Record<Uuid, CurriculumConstructor>
  sessionsByClassId: Record<Uuid, ClassSessionConstructor[]>
  curricula: QualityCheckCurriculumOption[]
  durationMs: number
  loadedAt: number
}

const rosterStudents = (
  allStudents: readonly StudentConstructor[],
  roster: CurriculumRoster
): StudentConstructor[] => {
  const studentsById = indexById(allStudents)
  return roster.studentIds
    .map((id) => studentsById[id])
    .filter((student): student is StudentConstructor => student != null)
}

export const buildQualityCheckSnapshot = (
  join: QualityCheckJoin,
  allStudents: readonly StudentConstructor[]
): QualityCheckSnapshot => {
  const students = rosterStudents(allStudents, join.roster)
  const classDocs = Object.values(join.classesById)
  const curriculumDocs = Object.values(join.curriculaById)

  return {
    curriculumId: join.curriculumId,
    semester: join.semester,
    students,
    classesById: join.classesById,
    curriculaById: join.curriculaById,
    sessionsByClassId: join.sessionsByClassId,
    stats: {
      rosterSize: join.roster.studentIds.length,
      rosterClassCount: join.roster.classIds.length,
      studentCount: students.length,
      classDocCount: classDocs.length,
      curriculumDocCount: curriculumDocs.length,
      approxBytes: approximateBytes([
        students,
        classDocs,
        curriculumDocs,
        Object.values(join.sessionsByClassId),
      ]),
      durationMs: join.durationMs,
      loadedAt: join.loadedAt,
    },
  }
}

/** Semester classes and their curricula: the dropdown's options and the roster's source. */
export const loadQualityCheckCatalog = async (
  semester: string
): Promise<QualityCheckCatalog> => {
  const fb = Firebase.getInstance()
  const semesterClasses = await fb.variableQuery<ClassConstructor>(
    DOC_TYPES.CLASSES,
    {
      queries: {
        semester: { comparator: COMPARATORS.EQUALS, value: semester },
      },
    }
  )
  const semesterCurricula = await fb.getDocumentsByIds<CurriculumConstructor>(
    DOC_TYPES.CURRICULUMS,
    uniq(semesterClasses.map((classObj) => classObj.curriculumId)).filter(
      Boolean
    )
  )
  const curriculaById = indexById(semesterCurricula)
  return {
    semester,
    semesterClasses,
    curriculaById,
    curricula: buildCurriculumCatalog(semesterClasses, curriculaById),
  }
}

/**
 * Class/curriculum read sequence, exported for tests. Student docs are taken
 * from `allStudents` (the global atom), not from Firestore.
 */
export const loadQualityCheckSnapshot = async (
  curriculumId: string,
  semester: string,
  allStudents: readonly StudentConstructor[],
  now: () => number = () => performance.now()
): Promise<QualityCheckSnapshot> => {
  const join = await loadQualityCheckJoin(
    curriculumId,
    semester,
    allStudents,
    now
  )
  return buildQualityCheckSnapshot(join, allStudents)
}

export const loadQualityCheckJoin = async (
  curriculumId: string,
  semester: string,
  allStudents: readonly StudentConstructor[],
  now: () => number = () => performance.now()
): Promise<QualityCheckJoin> => {
  const startedAt = now()
  const fb = Firebase.getInstance()

  const catalog = await loadQualityCheckCatalog(semester)
  const curriculaById = { ...catalog.curriculaById }

  const roster = resolveCurriculumRoster(catalog.semesterClasses, curriculumId)
  const students = rosterStudents(allStudents, roster)

  const classesById = indexById(catalog.semesterClasses)
  const missingClassIds = getMissingArtworkClassIds(
    students,
    new Set(Object.keys(classesById))
  )
  const artworkClasses = await fb.getDocumentsByIds<ClassConstructor>(
    DOC_TYPES.CLASSES,
    missingClassIds
  )
  for (const classObj of artworkClasses) classesById[classObj.id] = classObj

  const missingCurriculumIds = uniq(
    artworkClasses
      .map((classObj) => classObj.curriculumId)
      .filter((id) => id && !curriculaById[id])
  )
  const artworkCurricula = await fb.getDocumentsByIds<CurriculumConstructor>(
    DOC_TYPES.CURRICULUMS,
    missingCurriculumIds
  )
  for (const curriculum of artworkCurricula) {
    curriculaById[curriculum.id] = curriculum
  }

  const sessionClassIds = uniq([
    ...roster.classIds,
    ...artworkClasses.map((classObj) => classObj.id),
  ])
  const sessionIds = uniq(
    sessionClassIds.flatMap((id) => classesById[id]?.sessionIds ?? [])
  )
  const sessionDocs =
    sessionIds.length === 0
      ? []
      : await fb.getDocumentsByIds<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          sessionIds
        )

  return {
    curriculumId,
    semester,
    roster,
    classesById,
    curriculaById,
    sessionsByClassId: groupBy(sessionDocs, "classId"),
    curricula: catalog.curricula,
    durationMs: Math.round(now() - startedAt),
    loadedAt: Date.now(),
  }
}

const describeError = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : "Could not load artwork for this curriculum."

/**
 * Class/curriculum reads run when the curriculum changes or Refresh is
 * pressed, using whatever students are already in memory at that moment.
 * With no curriculum the read stops after the semester classes and their
 * curricula (the dropdown's options) and the status stays idle. The wall
 * itself reads `useGetAllStudents` on every render so artwork edits update
 * tiles without re-fetching semester classes.
 */
export const useQualityCheckData = (
  curriculumId: string | null
): QualityCheckData => {
  const allStudents = useGetAllStudents()
  const allStudentsRef = useRef(allStudents)
  allStudentsRef.current = allStudents

  const [status, setStatus] = useState<QualityCheckStatus>(
    QUALITY_CHECK_STATUS.IDLE
  )
  const [join, setJoin] = useState<QualityCheckJoin | null>(null)
  const [curricula, setCurricula] = useState<QualityCheckCurriculumOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)
  const generationRef = useRef(0)
  const selectedId = curriculumId || null

  const snapshot = useMemo(
    () => (join ? buildQualityCheckSnapshot(join, allStudents) : null),
    [allStudents, join]
  )

  useEffect(() => {
    generationRef.current += 1
    const generation = generationRef.current
    const semester = getCurrentSemester()

    const fail = (caught: unknown, nextStatus: QualityCheckStatus) => {
      if (generationRef.current !== generation) return
      Sentry.captureException(caught)
      setError(describeError(caught))
      setStatus(nextStatus)
    }

    setError(null)
    if (!selectedId) {
      setStatus(QUALITY_CHECK_STATUS.IDLE)
      setJoin(null)
      loadQualityCheckCatalog(semester)
        .then((catalog) => {
          if (generationRef.current !== generation) return
          setCurricula(catalog.curricula)
        })
        .catch((caught: unknown) => fail(caught, QUALITY_CHECK_STATUS.IDLE))
      return
    }

    setStatus(QUALITY_CHECK_STATUS.LOADING)
    setJoin((previous) =>
      previous && previous.curriculumId === selectedId ? previous : null
    )

    loadQualityCheckJoin(selectedId, semester, allStudentsRef.current)
      .then((next) => {
        if (generationRef.current !== generation) return
        setJoin(next)
        setCurricula(next.curricula)
        setStatus(QUALITY_CHECK_STATUS.READY)
      })
      .catch((caught: unknown) => fail(caught, QUALITY_CHECK_STATUS.ERROR))
  }, [selectedId, refreshToken])

  const refresh = useCallback(() => {
    setRefreshToken((token) => token + 1)
  }, [])

  return { status, snapshot, error, curricula, refresh }
}
